// DeviceListTableRow – single device row with live status; details open in a dialog.

import React, { JSX, useMemo } from 'react';
import {
    DeviceMasterRow,
    ExtraColumnKey,
    DeviceStatus,
} from '../../../../types/DeviceListTypes';
import { toMillis, formatTime, formatUserIdentityDisplay, normStatus, formatElapsed } from '../../../helpers/helpers';
import type { CommandDdbItem } from '../../../../types/Commands';
import { getAckPercent, parseStatusInfo, isFailedStatus, pickCommandTsMs } from '../../CommandListView/CommandsListHelpers';
import {
    ModeBadge,
    battColourClass,
    formatBatteryLabel,
    formatLastHeardLabel,
    pickMode,
} from './deviceLiveUi';
import type { ModeKey } from '../types/DeviceLiveTypes';
import { lifecycleFlagYesNo, normalizeLifecycleSnapshot } from '../../../helpers/deviceLifecycle';
import { formatDeviceFeedbackReason, toCommandUpdatedMs } from '../utils/deviceListHelpers';

// ---- Types ----

interface DeviceTableRowProps {
    row: DeviceMasterRow;
    columns: string[];
    visibleMasterColumns: string[];
    visibleExtraColumns: ExtraColumnKey[];
    status?: DeviceStatus;
    forcedBusy?: boolean;
    /** True when the paired front/rear device has an active (non-terminal) command. */
    pairCommandActive?: boolean;
    tls?: boolean | undefined;
    online: boolean;
    selected: boolean;
    cmdStatus?: string;
    latestCommand?: CommandDdbItem;
    nowMs?: number;
    onToggleSelect: () => void;
    onRowClick: () => void;
    onOpenDeviceDetails: () => void;
    onCenterMapOnLocation?: (lat: number, lng: number, deviceId?: string) => void;
    operatorScopedEventId?: string | null;
    lifecycle?: Record<string, unknown> | null;
    attributes?: Record<string, unknown> | null;
    validationFailureReasons?: string[];
    userIdToDisplayName?: Record<string, string>;
}

// ---- Constants ----

export const PROGRESS_STROBE_CSS = `
@keyframes rrProgressStrobe {
  0%   { transform: translateX(-250%); opacity: 0.25; }
  50%  { opacity: 0.55; }
  100% { transform: translateX(250%); opacity: 0.25; }
}
`;

// ---- Helpers ----

/** Sort order for mode column. */
function modeRank(mode: ModeKey): number {
    if (mode === 'unknown') return 0;
    if (mode === 'app') return 1;
    if (mode === 'charging') return 2;
    if (mode === 'race') return 3;
    return 4;
}

/** When a master CSV cell is exactly a known Cognito sub/userId, show email (or mapped label). */
function formatMasterCellForDisplay(raw: unknown, userIdToDisplayName: Record<string, string>): string {
    const s = String(raw ?? '').trim();
    if (!s) return '';
    if (Object.prototype.hasOwnProperty.call(userIdToDisplayName, s)) {
        return formatUserIdentityDisplay(s, userIdToDisplayName);
    }
    return s;
}

/** Lat/lng from device status for the Location column. */
function latLngFromStatus(status: DeviceStatus | undefined): { label: string; title: string } {
    const lat = status?.lat;
    const lng = status?.lng;
    if (typeof lat === 'number' && typeof lng === 'number' && Number.isFinite(lat) && Number.isFinite(lng)) {
        const label = `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
        return { label, title: label };
    }
    return { label: '', title: '' };
}

function renderExtraCell(params: {
    key: ExtraColumnKey;
    onlineClass: string;
    onlineLabel: string;
    mode: ModeKey;
    modeSort: number;
    fwLabel: string;
    modemFwLabel: string;
    gnssFwLabel: string;
    battLabel: string;
    battClass: string;
    lastHeardLabel: string;
    tlsLabel: string;
    tlsClass: string;
    raceLogLabel: string;
    debugLogLabel: string;
    battMaLabel: string;
    puckLabel: string;
    puckClass: string;
    reasonLabel: string;
    statusStaleClass: string;
    locationLabel: string;
    locationTitle: string;
    locationUpdatedLabel: string;
    locationLat?: number;
    locationLng?: number;
    locationDeviceId?: string;
    onCenterMapOnLocation?: (lat: number, lng: number, deviceId?: string) => void;

    lastCmdIdLabel: string;
    lastCmdTypeLabel: string;
    lastCmdTargetLabel: string;
    lastCmdStatusContent: React.ReactNode;
    lastCmdAckProgressContent: React.ReactNode;
    lastCmdUpdatedLabel: string;
    lifecycleSnap: ReturnType<typeof normalizeLifecycleSnapshot>;
    attrRaceCountLabel: string;
    attrWaterproofedByLabel: string;
}): JSX.Element {
    const {
        key,
        onlineClass,
        onlineLabel,
        mode,
        modeSort,
        fwLabel,
        modemFwLabel,
        gnssFwLabel,
        battLabel,
        battClass,
        lastHeardLabel,
        tlsLabel,
        tlsClass,
        raceLogLabel,
        debugLogLabel,
        battMaLabel,
        puckLabel,
        puckClass,
        reasonLabel,
        statusStaleClass,
        locationLabel,
        locationTitle,
        locationLat,
        locationLng,
        locationDeviceId,
        onCenterMapOnLocation,

        lastCmdIdLabel,
        lastCmdTypeLabel,
        lastCmdTargetLabel,
        lastCmdStatusContent,
        lastCmdAckProgressContent,
        lastCmdUpdatedLabel,
    } = params;

    // Render a single extra column by key.
    switch (key) {
        case '__online':
            return <td className={`px-4 py-2 ${onlineClass}`}>{onlineLabel}</td>;
        case '__mode':
            return (
                <td className="px-4 py-2" data-sort={modeSort}>
                    <ModeBadge mode={mode} />
                </td>
            );
        case '__fw':
            return <td className={`px-4 py-2 ${statusStaleClass}`}>{fwLabel}</td>;
        case '__modem_fw':
            return (
                <td className={`px-4 py-2 max-w-[14rem] truncate text-xs ${statusStaleClass}`} title={modemFwLabel || undefined}>
                    {modemFwLabel || '—'}
                </td>
            );
        case '__gnss_fw':
            return (
                <td className={`px-4 py-2 max-w-[14rem] truncate text-xs ${statusStaleClass}`} title={gnssFwLabel || undefined}>
                    {gnssFwLabel || '—'}
                </td>
            );
        case '__batt':
            return (
                <td className={`px-4 py-2 ${statusStaleClass}`}>
                    <div className={`inline-flex items-center justify-center px-2 py-1 rounded-md text-xs font-semibold ${battClass}`}>
                        {battLabel || '—'}
                    </div>
                </td>
            );
        case '__last':
            return <td className={`px-4 py-2 ${statusStaleClass}`}>{lastHeardLabel}</td>;
        case '__tls':
            return <td className={`px-4 py-2 ${tlsClass}`}>{tlsLabel}</td>;
        case '__lifecycle_lost':
            return (
                <td className="px-4 py-2 text-center text-xs font-semibold tabular-nums text-gray-800 dark:text-slate-200">
                    {lifecycleFlagYesNo(!!params.lifecycleSnap?.lost)}
                </td>
            );
        case '__lifecycle_decommissioned':
            return (
                <td className="px-4 py-2 text-center text-xs font-semibold tabular-nums text-gray-800 dark:text-slate-200">
                    {lifecycleFlagYesNo(!!params.lifecycleSnap?.decommissioned)}
                </td>
            );
        case '__lifecycle_damaged':
            return (
                <td className="px-4 py-2 text-center text-xs font-semibold tabular-nums text-gray-800 dark:text-slate-200">
                    {lifecycleFlagYesNo(!!params.lifecycleSnap?.damaged)}
                </td>
            );
        case '__lifecycle_errored':
            return (
                <td className="px-4 py-2 text-center text-xs font-semibold tabular-nums text-gray-800 dark:text-slate-200">
                    {lifecycleFlagYesNo(!!params.lifecycleSnap?.errored)}
                </td>
            );
        case '__lifecycle_note': {
            const note = String(params.lifecycleSnap?.reason ?? '').trim();
            return (
                <td className="px-4 py-2 max-w-[14rem] truncate text-xs text-gray-700 dark:text-slate-300" title={note || undefined}>
                    {note || '—'}
                </td>
            );
        }
        case '__attr_race_count':
            return (
                <td className="px-4 py-2 text-right text-xs tabular-nums text-gray-800 dark:text-slate-200">
                    {params.attrRaceCountLabel || '—'}
                </td>
            );
        case '__attr_waterproofed_by':
            return (
                <td className="px-4 py-2 text-xs text-gray-700 dark:text-slate-300">
                    {params.attrWaterproofedByLabel || '—'}
                </td>
            );
        case '__race_log':
            return <td className={`px-4 py-2 ${statusStaleClass}`}>{raceLogLabel}</td>;
        case '__debug_log':
            return <td className={`px-4 py-2 ${statusStaleClass}`}>{debugLogLabel}</td>;
        case '__batt_ma':
            return <td className={`px-4 py-2 ${statusStaleClass}`}>{battMaLabel}</td>;
        case '__reason':
            return (
                <td
                    className={`px-4 py-2 max-w-[16rem] whitespace-nowrap overflow-hidden text-ellipsis ${statusStaleClass}`}
                    title={reasonLabel || undefined}
                >
                    {reasonLabel || '—'}
                </td>
            );
        case '__gga': {
            const hasLocation = typeof params.locationLat === 'number' && typeof params.locationLng === 'number';
            const clickable = hasLocation && params.onCenterMapOnLocation;
            return (
                <td
                    className={`px-4 py-2 text-xs font-mono max-w-[200px] truncate ${statusStaleClass}`}
                    title={
                        params.locationTitle ||
                        params.locationLabel ||
                        (clickable ? 'Click to center map' : undefined)
                    }
                >
                    {clickable ? (
                        <button
                            type="button"
                            className="text-left w-full truncate cursor-pointer hover:underline hover:text-blue-700 text-inherit"
                            onClick={(e) => {
                                e.stopPropagation();
                                if (params.locationLat != null && params.locationLng != null) {
                                    params.onCenterMapOnLocation?.(params.locationLat, params.locationLng, params.locationDeviceId);
                                }
                            }}
                        >
                            {params.locationLabel || '—'}
                        </button>
                    ) : (
                        params.locationLabel || '—'
                    )}
                </td>
            );
        }
        case '__location_updated':
            return <td className={`px-4 py-2 whitespace-nowrap ${statusStaleClass}`}>{params.locationUpdatedLabel || '—'}</td>;

        case '__last_cmd_id':
            return <td className="px-4 py-2">{lastCmdIdLabel}</td>;
        case '__last_cmd_type':
            return <td className="px-4 py-2">{lastCmdTypeLabel}</td>;
        case '__last_cmd_target':
            return <td className="px-4 py-2">{lastCmdTargetLabel}</td>;
        case '__last_cmd_status':
            return <td className="px-4 py-2">{lastCmdStatusContent}</td>;
        case '__last_cmd_ack_percent':
            return <td className="px-4 py-2">{lastCmdAckProgressContent}</td>;
        case '__last_cmd_updated':
            return <td className="px-4 py-2">{lastCmdUpdatedLabel}</td>;
        default:
            return <td className="px-4 py-2" />;
    }
}

// ---- Component ----

function DeviceTableRowImpl(props: DeviceTableRowProps): JSX.Element {
    const {
        row,
        columns,
        visibleMasterColumns,
        visibleExtraColumns,
        status,
        forcedBusy = false,
        pairCommandActive = false,
        tls,
        online,
        selected,
        cmdStatus,
        latestCommand,
        nowMs,
        onToggleSelect,
        onRowClick,
        onOpenDeviceDetails,
        onCenterMapOnLocation,
        operatorScopedEventId,
        lifecycle,
        attributes,
        validationFailureReasons,
        userIdToDisplayName = {},
    } = props;

    // Prefer real-time ACK-sourced status; fall back to command record and prop.
    const rawCmdStatus = (status as DeviceStatus)?.last_cmd_status || latestCommand?.status || cmdStatus || '';
    const cmdStatusNorm = normStatus(rawCmdStatus);

    const effectiveOnline = online;

    const baseMode = pickMode(effectiveOnline, status);
    const mode =
        baseMode === 'unknown' || baseMode === 'race'
            ? baseMode
            : (forcedBusy ? 'busy' : baseMode);
    const modeSort = modeRank(mode);

    const idKey = columns[0];
    const deviceId =
        typeof row?.[idKey] === 'string' ? (row[idKey] as string).trim() : '';
    const masterColumnsExcludingId = columns.slice(1);

    let tlsLabel = 'n/a';
    let tlsClass = '';
    if (tls === true) {
        tlsLabel = 'Provisioned';
        tlsClass = 'text-green-700';
    } else if (tls === false) {
        tlsLabel = 'Unprovisioned';
        tlsClass = 'text-red-700';
    }

    const fwLabel = (status?.fw as string) || '';
    const modemFwLabel = (status?.modem_fw as string) || '';
    const gnssFwLabel = (status?.gnss_fw as string) || '';

    const statusStale = !effectiveOnline || mode === 'race';
    const statusStaleClass = statusStale ? 'text-gray-500 dark:text-slate-400' : '';

    const battLabel = effectiveOnline ? formatBatteryLabel(status) : 'unknown';

    const battClass = statusStale
        ? 'bg-gray-100 dark:bg-slate-800 text-gray-500'
        : effectiveOnline
        ? battColourClass(status?.batt_mv)
        : 'bg-gray-100 dark:bg-slate-800 text-gray-500';

    const lastHeardLabel = formatLastHeardLabel(status);

    const raceLogLabel =
        typeof status?.race_log_size === 'number'
            ? String(status.race_log_size)
            : '';

    const debugLogLabel =
        typeof status?.debug_log_size === 'number'
            ? String(status.debug_log_size)
            : '';

    const battMaLabel =
        typeof status?.batt_ma === 'number' ? String(status.batt_ma) : '';

    const busyFlag = typeof (status as any)?.busy === 'number' ? Number((status as any).busy) : 0;
    const busyFrom = typeof (status as any)?.busy_from === 'number' ? Number((status as any).busy_from) : 0;
    // forcedBusy reflects an in-flight command (PENDING/IN_PROGRESS) even before the device's own
    // telemetry reports busy=1 — without it, fast devices can finish a command between heartbeats
    // and never visually show as busy at all.
    const busy = busyFlag > 0 || forcedBusy;
    const reasonLabel = formatDeviceFeedbackReason(status);

    const { label: locationLabel, title: locationTitle } = latLngFromStatus(status);
    const locationUpdatedLabel = formatTime(status?.last_location_updated_at);

    const lastCmdIdLabel = latestCommand?.commandId || '';
    const lastCmdTypeLabel = latestCommand?.type || '';
    const lastCmdTargetLabel = latestCommand?.targetVersion || '';

    const lastCmdUpdatedLabel =
        typeof latestCommand?.updatedAt === 'number'
            ? formatTime(latestCommand.updatedAt)
            : '';

    const now = typeof nowMs === 'number' ? nowMs : 0;
    const showTimer = busy && now > 0 && busyFrom > 0;
    const elapsedMs = showTimer ? now - toMillis(busyFrom) : 0;

    const ackProgress = useMemo(() => {
        const raw = latestCommand?.status_info;
        if (!raw || typeof raw !== 'string') return null;
        return parseStatusInfo(raw);
    }, [latestCommand?.status_info]);

    const lastCmdStatusContent = (
        <span className="inline-flex items-center">
            <span>{rawCmdStatus || '-'}</span>
            {showTimer && (
                <span className="ml-2 text-[11px] text-gray-500 dark:text-slate-400 tabular-nums">
                    {formatElapsed(elapsedMs)}
                </span>
            )}
        </span>
    );

    const pctRaw = getAckPercent(ackProgress || undefined);
    const hasPct = typeof pctRaw === 'number' && Number.isFinite(pctRaw) && pctRaw >= 0;
    const pct = hasPct ? Math.max(0, Math.min(100, pctRaw)) : 0;
    const isTerminalCmdStatus =
        cmdStatusNorm === 'SUCCEEDED'
        || cmdStatusNorm === 'FAILED'
        || cmdStatusNorm === 'TIMED_OUT'
        || cmdStatusNorm === 'CANCELLED';

    // The latest command record can be an old, finished operation while the busy state
    // belongs to newer untracked work — a rear relaying its front's upload creates no
    // command record on the rear. Only let a terminal status suppress the progress cell
    // when the busy state plausibly belongs to that command: if telemetry busy began
    // AFTER the command finished, or the partner device's command is still running,
    // the device is on newer work and keeps its strobe.
    const cmdUpdatedMs = toCommandUpdatedMs(latestCommand?.updatedAt);
    const busyIsNewerThanCmd =
        pairCommandActive || (busyFlag > 0 && busyFrom > 0 && toMillis(busyFrom) > cmdUpdatedMs);
    const terminalHidesProgress = isTerminalCmdStatus && !busyIsNewerThanCmd;

    const showStrobe = busy && !terminalHidesProgress;
    // Ops that never send progress acks (modem DFOTA, mode changes, reboot, …)
    // stay indeterminate: strobe only, no fabricated percentage.
    const pctLabel = hasPct ? String(pct) : '';
    const hideProgress = terminalHidesProgress;

    const lastCmdAckProgressContent = (!hideProgress && (hasPct || busy)) ? (
        <div className="flex items-center gap-2">
            <div className="w-24 h-2 bg-gray-200 rounded overflow-hidden relative">
                {hasPct && (
                    <div className="h-2 rounded bg-blue-500" style={{ width: `${pct}%` }} />
                )}
                {showStrobe && (
                    <div
                        className="absolute top-0 left-0 h-2 w-1/3 rounded bg-blue-500"
                        style={{ animation: 'rrProgressStrobe 2.0s linear infinite' }}
                    />
                )}
            </div>
            <span className="text-[10px] text-gray-700 dark:text-slate-300 tabular-nums w-8 text-right">
                {pctLabel}
            </span>
        </div>
    ) : (
        <span className="text-xs text-gray-500 dark:text-slate-400">—</span>
    );


    const puckValue =
        effectiveOnline && typeof status?.puck === 'number'
            ? status.puck
            : 0;

    const puckLabel = String(puckValue);

    const puckClass =
        puckValue > 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';

    const onlineLabel = effectiveOnline ? 'Online' : 'Offline';
    const onlineClass = effectiveOnline ? 'text-green-700' : 'text-red-700';

    const lifecycleSnap = normalizeLifecycleSnapshot(lifecycle);

    const attrRaceCountRaw = attributes?.raceCount;
    const attrRaceCountLabel =
        typeof attrRaceCountRaw === 'number' && Number.isFinite(attrRaceCountRaw)
            ? String(attrRaceCountRaw)
            : '';
    const attrWaterproofedByLabel = String(attributes?.waterproofedBy ?? '').trim();

    let baseRowClass = 'border-t transition-colors';
    const validationFailed = Array.isArray(validationFailureReasons) && validationFailureReasons.length > 0;
    const lifecycleErrored = !!lifecycleSnap?.errored;
    const commandErrored = isFailedStatus(cmdStatusNorm);
    const hasErrorHighlight = validationFailed || lifecycleErrored || commandErrored;

    if (!effectiveOnline) {
        baseRowClass += ' bg-gray-100 dark:bg-slate-800/80 hover:bg-gray-200 dark:hover:bg-slate-700/80';
    } else if (mode === 'race') {
        baseRowClass += ' bg-slate-50 dark:bg-slate-900/80 hover:bg-slate-100 dark:hover:bg-slate-800/90';
    } else if (busy) {
        baseRowClass += ' bg-amber-50 dark:bg-amber-950/20 hover:bg-amber-100 dark:hover:bg-amber-900/30';
    } else {
        baseRowClass += ' bg-white dark:bg-slate-900 hover:bg-gray-50 dark:hover:bg-slate-800/60';
    }

    if (selected) {
        baseRowClass += ' ring-1 ring-inset ring-blue-300 dark:ring-blue-700/60';
    }
    if (hasErrorHighlight) {
        baseRowClass += ' bg-red-50 dark:bg-red-950/25 hover:bg-red-100 dark:hover:bg-red-950/35 ring-1 ring-inset ring-red-200 dark:ring-red-800/70';
    }

    const rowProblems: string[] = [];
    if (validationFailed) {
        rowProblems.push(`Validation failed: ${(validationFailureReasons || []).join('; ')}`);
    }
    if (lifecycleErrored) {
        rowProblems.push('Lifecycle marked errored');
    }
    if (commandErrored) {
        rowProblems.push(`Last command failed (${rawCmdStatus || cmdStatusNorm})`);
    }
    const rowTitle = rowProblems.length > 0 ? rowProblems.join(' | ') : undefined;

    return (
        <>
            {/* Main row: checkbox, id + more info, master cols, extra cols (mode, battery, last cmd, etc.) */}
            <tr
                className={baseRowClass}
                onClick={onRowClick}
                aria-busy={busy ? 'true' : 'false'}
                title={rowTitle}
                data-device-id={deviceId || undefined}
            >
                <td className="px-4 py-2" onClick={(e) => e.stopPropagation()}>
                    {deviceId && (
                        <input type="checkbox" checked={selected} onChange={onToggleSelect} />
                    )}
                </td>

                {idKey && (
                    <td className="px-4 py-2">
                        {deviceId ? (
                            <button
                                type="button"
                                className="cursor-pointer rounded px-0.5 text-left font-mono text-sm text-blue-700 dark:text-blue-300 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onOpenDeviceDetails();
                                }}
                            >
                                {String(row[idKey] ?? '')}
                            </button>
                        ) : (
                            <span>{row[idKey] ?? ''}</span>
                        )}
                    </td>
                )}

                {masterColumnsExcludingId.map((col) =>
                    visibleMasterColumns.includes(col) ? (
                        <td key={col} className="px-4 py-2">
                            {formatMasterCellForDisplay(row[col], userIdToDisplayName)}
                        </td>
                    ) : null,
                )}

                {visibleExtraColumns.map((key) => (
                    <React.Fragment key={key}>
                        {renderExtraCell({
                            key,
                            onlineClass,
                            onlineLabel,
                            mode,
                            modeSort,
                            fwLabel,
                            modemFwLabel,
                            gnssFwLabel,
                            battLabel,
                            battClass,
                            lastHeardLabel,
                            tlsLabel,
                            tlsClass,
                            raceLogLabel,
                            debugLogLabel,
                            battMaLabel,
                            puckLabel,
                            puckClass,
                            reasonLabel,
                            statusStaleClass,
                            locationLabel,
                            locationTitle,
                            locationUpdatedLabel,
                            locationLat: typeof status?.lat === 'number' ? status.lat : undefined,
        locationLng: typeof status?.lng === 'number' ? status.lng : undefined,
        locationDeviceId: deviceId,
        onCenterMapOnLocation,

        lastCmdIdLabel,
        lastCmdTypeLabel,
                            lastCmdTargetLabel,
                            lastCmdStatusContent,
                            lastCmdAckProgressContent,
                            lastCmdUpdatedLabel,
                            lifecycleSnap,
                            attrRaceCountLabel,
                            attrWaterproofedByLabel,
                        })}
                    </React.Fragment>
                ))}
            </tr>
        </>
    );
}

/** True when a row is busy and therefore cares about the 1s `nowMs` ticker. */
function rowIsBusy(props: DeviceTableRowProps): boolean {
    if (props.forcedBusy) return true;
    const busy = (props.status as { busy?: unknown } | undefined)?.busy;
    return typeof busy === 'number' && busy > 0;
}

/**
 * Re-render a row only when data that affects its output actually changes.
 *
 * Callback props (onToggleSelect/onRowClick/onOpenDeviceDetails/onCenterMapOnLocation)
 * are intentionally NOT compared: they use functional state updates and stable setters,
 * so an identity change does not change behaviour. The `nowMs` ticker is ignored for
 * idle rows so the per-second busy timer doesn't re-render the whole table.
 */
function rowPropsEqual(a: DeviceTableRowProps, b: DeviceTableRowProps): boolean {
    if (
        a.row !== b.row ||
        a.columns !== b.columns ||
        a.visibleMasterColumns !== b.visibleMasterColumns ||
        a.visibleExtraColumns !== b.visibleExtraColumns ||
        a.status !== b.status ||
        a.forcedBusy !== b.forcedBusy ||
        a.pairCommandActive !== b.pairCommandActive ||
        a.tls !== b.tls ||
        a.online !== b.online ||
        a.selected !== b.selected ||
        a.cmdStatus !== b.cmdStatus ||
        a.latestCommand !== b.latestCommand ||
        a.operatorScopedEventId !== b.operatorScopedEventId ||
        a.lifecycle !== b.lifecycle ||
        a.attributes !== b.attributes ||
        a.validationFailureReasons !== b.validationFailureReasons ||
        a.userIdToDisplayName !== b.userIdToDisplayName
    ) {
        return false;
    }

    // Only busy rows display the elapsed timer that depends on `nowMs`.
    if ((rowIsBusy(a) || rowIsBusy(b)) && a.nowMs !== b.nowMs) return false;

    return true;
}

export const DeviceTableRow = React.memo(DeviceTableRowImpl, rowPropsEqual);